import { useParams } from "react-router";
import { v4 } from "uuid";
import { useVbsWorkOrderShowQuery } from "@/services/backend/vbs/work-orders/api/work-order-api";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { WorkOrderPdf } from "@/services/backend/vbs/work-orders/components/work-order-pdf";
import { WorkOrder } from "@/services/backend/vbs/work-orders/types/work-order";
import { Separator } from "@/shared/components/ui/separator";
import { CancelWorkOrderDialog } from "@/services/backend/vbs/work-orders/components/cancel-work-order-dialog";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/vbs/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { FulfillmentStateBadge } from "@/services/backend/relationships/components/fulfillment-state-badge";
import { StartExecutionDialog } from "@/services/backend/vbs/work-orders/components/start-execution-dialog";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { useState } from "react";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/vbs/work-orders/_components/work-order-breadcrumbs";

export function WorkOrderPlanningRoute() {
  const { workOrderId } = useParams();
  const {
    data: workOrder,
    isLoading,
    error,
  } = useVbsWorkOrderShowQuery({
    id: workOrderId!,
  });

  useWorkOrderStateGuard(
    workOrder,
    FulfillmentState.Planning,
    FulfillmentState.Zero,
  );

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="planning">
      {t("Planung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && (
        <PersistenceContextProvider>
          <Page workOrder={workOrder} />
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  const links = [
    {
      to: "base-data",
      label: t("Daten"),
    },
    {
      to: "pricing",
      label: t("Preise"),
    },
    {
      to: "calculation",
      label: t("Kalkulation"),
    },
    {
      to: "treatment",
      label: t("Behandlung"),
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  const [key, setKey] = useState(v4());

  return (
    <DocumentPage>
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink key={link.to} to={link.to}>
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <FulfillmentStateBadge state={workOrder.state} />
        <DocumentPageActions>
          <PersistenceStateUsingContext className="mr-2" />
          <Separator orientation="vertical" className="min-h-8" />
          <WorkOrderPdf workOrderId={workOrder.id} />
          <CpyDialog
            destinationEntityName={CpyName.VBSWorkOrder}
            destinationId={workOrder.id}
            onCpySuccess={() => {
              // Await refetch from server
              setTimeout(() => {
                setKey(v4());
              }, 1000);
            }}
          />
          <Separator orientation="vertical" className="min-h-8" />
          <CancelWorkOrderDialog workOrder={workOrder} />
          <StartExecutionDialog workOrder={workOrder} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent key={key}>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
